/**
 * All Services Page - Reusable component for service catalog
 * Task 39: Service catalog pages for 6 service-based industries
 */

import { useState } from 'react';
import { CompanyType } from '@/types/company';
import { ServiceList } from '../shared/ServiceList';
import { ServiceForm } from '../shared/ServiceForm';
import { useIndustryServices } from '@/hooks/industry';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { IndustryService } from '@/types/industry';

interface AllServicesPageProps {
  domain: CompanyType;
  pageTitle: string;
  pageDescription: string;
}

export function AllServicesPage({ domain, pageTitle, pageDescription }: AllServicesPageProps) {
  const { services, isLoading, createService, updateService, deleteService } = useIndustryServices({
    domain,
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingService, setEditingService] = useState<IndustryService | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extract existing categories
  const existingCategories = Array.from(
    new Set(services?.filter((s) => s.category).map((s) => s.category!) || [])
  ).sort();

  const handleCreate = () => {
    setEditingService(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (service: IndustryService) => {
    setEditingService(service);
    setIsFormOpen(true);
  };

  const handleDelete = async (service: IndustryService) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${service.name}"?`)) {
      return;
    }

    try {
      await deleteService(service.id);
      toast.success('Serviço excluído com sucesso');
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Erro ao excluir serviço');
    }
  };

  const handleToggleActive = async (service: IndustryService) => {
    try {
      await updateService({
        id: service.id,
        data: { is_active: !service.is_active },
      });
      toast.success(`Serviço ${service.is_active ? 'desativado' : 'ativado'} com sucesso`);
    } catch (error) {
      console.error('Error toggling service:', error);
      toast.error('Erro ao atualizar status do serviço');
    }
  };

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      if (editingService) {
        await updateService({
          id: editingService.id,
          data,
        });
        toast.success('Serviço atualizado com sucesso');
      } else {
        await createService(data);
        toast.success('Serviço criado com sucesso');
      }
      setIsFormOpen(false);
      setEditingService(undefined);
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Erro ao salvar serviço');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Carregando serviços...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">{pageTitle}</h1>
        <p className="text-muted-foreground">{pageDescription}</p>
      </div>

      {/* Service List */}
      <ServiceList
        services={services || []}
        domain={domain}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
        onCreate={handleCreate}
      />

      {/* Service Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingService ? 'Editar' : 'Novo'} Serviço
            </DialogTitle>
            <DialogDescription>
              {editingService
                ? 'Atualize as informações do serviço'
                : 'Preencha as informações do novo serviço'}
            </DialogDescription>
          </DialogHeader>
          <ServiceForm
            service={editingService}
            domain={domain}
            existingCategories={existingCategories}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsFormOpen(false);
              setEditingService(undefined);
            }}
            isSubmitting={isSubmitting}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
